import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  MarkAllReadResponse,
  NotificationPageResponse,
  NotificationSearchParams,
  UnreadCountResponse,
  UserNotification,
} from '../models/notification.model';

/**
 * API HTTP del módulo de Notificaciones in-app.
 *
 * Multi-tenant: el `businessId` (del usuario logueado) viaja primero en cada
 * request que lo requiere; el `userId` destinatario lo resuelve el backend desde
 * el JWT (nunca se envía). Las rutas de marcado (`/{id}/read`) no llevan params.
 */
@Injectable({ providedIn: 'root' })
export class NotificationApiService {
  private readonly apiUrl = `${environment.apiUrl}api/notifications`;

  constructor(
    private http: HttpClient,
    private auth: AuthService
  ) {}

  /** GET /api/notifications — lista paginada (más recientes primero). */
  search(params: NotificationSearchParams = {}): Observable<NotificationPageResponse> {
    let httpParams = new HttpParams().set('businessId', this.auth.getBusinessId() ?? '');
    if (params.page !== undefined) httpParams = httpParams.set('page', params.page);
    if (params.size !== undefined) httpParams = httpParams.set('size', params.size);
    if (params.unreadOnly !== undefined) httpParams = httpParams.set('unreadOnly', params.unreadOnly);

    return this.http.get<NotificationPageResponse>(this.apiUrl, { params: httpParams });
  }

  /** GET /api/notifications/unread-count — conteo barato para polling. */
  getUnreadCount(): Observable<UnreadCountResponse> {
    const params = new HttpParams().set('businessId', this.auth.getBusinessId() ?? '');
    return this.http.get<UnreadCountResponse>(`${this.apiUrl}/unread-count`, { params });
  }

  /** PUT /api/notifications/{id}/read — marca una notificación como leída (idempotente). */
  markRead(id: number): Observable<UserNotification> {
    return this.http.put<UserNotification>(`${this.apiUrl}/${id}/read`, {});
  }

  /** PUT /api/notifications/read-all — marca todas las no leídas como leídas. */
  markAllRead(): Observable<MarkAllReadResponse> {
    const params = new HttpParams().set('businessId', this.auth.getBusinessId() ?? '');
    return this.http.put<MarkAllReadResponse>(`${this.apiUrl}/read-all`, {}, { params });
  }
}
